// From the package root, not `.../src/API`: jest's moduleNameMapper maps the
// deep path to the index, and this class would extend `undefined` under test.
import { API } from 'decap-cms-backend-github';
import { branchFromContentKey, DEFAULT_PR_BODY } from 'decap-cms-lib-util';

/** Matches the REST path's result, so this stays a drop-in subclass. */

// Derived rather than imported from `decap-cms-backend-github/src/API`: the
// published package ships that directory as TypeScript source, which no
// consumer's bundler is configured to resolve.

/**
 * Statuses that prove the server did NOT commit, and so are safe to retry on
 * the REST path:
 *
 * - 404: the deployed edge function predates this endpoint. Falling back keeps
 *   a CMS bundle safe to ship before the server side is live.
 * - 413: the payload exceeded the endpoint's ceiling, rejected during
 *   validation before any GitHub call.
 * - 400: rejected by request validation, also before any GitHub call. This is
 *   what makes the two repos safe to deploy in either order: a request shape
 *   the deployed function does not understand yet saves over REST instead.
 *
 * Nothing else falls back. A network error or a 5xx could mean the commit
 * landed and the response was lost, and retrying that would double-commit.
 */
const FALLBACK_STATUSES = new Set([400, 404, 413]);

/**
 * GitHub API client that commits through Turbo's `_content/commit` endpoint in
 * one request, instead of the REST blob/tree/commit/ref sequence's `N + 4`.
 */
export default class TurboAPI extends API {
  constructor(config) {
    super(config);
    this.turboFetch = config.turboFetch;
  }
  get commitEndpoint() {
    return `${this.apiRoot}/_content/commit`;
  }

  /**
   * Renames stay on REST: Decap moves sibling files by blob sha, and
   * `_content/commit` takes contents, not shas.
   *
   * Editorial workflow: only a FIRST save, the one that puts an entry into
   * review. Re-saving an unpublished entry rebases its branch and computes the
   * media to drop from a client-side diff, which is not a plain commit.
   */
  canCommitViaTurbo(dataFiles, options) {
    if (!this.turboFetch) {
      return false;
    }
    if (dataFiles.some(file => file.newPath)) {
      return false;
    }
    if (options.useWorkflow) {
      return !options.unpublished;
    }
    return true;
  }

  /** The entry's workflow branch by Decap's own rule, so server and client agree. */
  workflowBranchFor(options, slug) {
    return branchFromContentKey(this.generateContentKey(options.collectionName, slug));
  }

  /**
   * Finishes a workflow save whose commit landed but whose pull request or
   * label did not. Never re-runs the commit, which would add a duplicate.
   */
  async finishWorkflowOverRest(branch, slug, options, pullRequestNumber) {
    console.warn('Turbo committed the entry but could not put it into review; finishing over REST.');
    if (!pullRequestNumber) {
      await this.createPR(options.commitMessage, branch);
    }

    // Re-reads the pull request so its non-CMS labels survive the status
    // change, which is why this is not a bare updatePullRequestLabels.
    await this.updateUnpublishedEntryStatus(options.collectionName, slug, options.status || this.initialWorkflowStatus);
  }

  /**
   * `asset` keeps media out of the content cache; only the client knows the
   * data-file / asset split. Cache bookkeeping only: the server checks
   * permissions on every path regardless.
   */
  async toCommitAddition(file, asset) {
    const contents = typeof file.toBase64 === 'function' ? await file.toBase64() : await this.toBase64(file.raw);
    return {
      path: file.path.replace(/^\/+/, ''),
      contents,
      ...(asset && {
        asset: true
      })
    };
  }
  async turboCommit(body) {
    const response = await this.turboFetch(this.commitEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    return await response.json();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shouldFallBack(error) {
    return FALLBACK_STATUSES.has(error?.status);
  }
  async persistFiles(dataFiles, mediaFiles, options) {
    if (!this.canCommitViaTurbo(dataFiles, options)) {
      return super.persistFiles(dataFiles, mediaFiles, options);
    }

    // Media first, matching the REST path's order.
    try {
      const additions = await Promise.all([...mediaFiles.map(file => this.toCommitAddition(file, true)), ...dataFiles.map(file => this.toCommitAddition(file, false))]);

      // A workflow save targets the entry's own branch; the server opens the
      // pull request and applies the status label in the same call.
      const slug = dataFiles[0]?.slug;
      const workflowBranch = options.useWorkflow && slug ? this.workflowBranchFor(options, slug) : null;
      const commit = await this.turboCommit({
        branch: workflowBranch ?? this.branch,
        // The branch this deploy edits, which the server cannot infer: one site
        // can serve several deploys of a repo, and without it a staging draft
        // forked from production.
        base: this.branch,
        ...(workflowBranch && {
          workflow: {
            status: options.status || this.initialWorkflowStatus,
            label_prefix: this.cmsLabelPrefix || undefined,
            pull_request_title: options.commitMessage,
            pull_request_body: DEFAULT_PR_BODY
          }
        }),
        message: options.commitMessage,
        additions,
        // Descriptive only: what the Deploys page shows in place of a bare sha.
        entry_label: options.entryLabel,
        entry_path: options.entryPath
      });

      // The commit has landed by this point. If the pull request or the label
      // did not, the entry exists on its branch but is not in review, so the
      // remaining steps are redone over REST — never the commit itself.
      if (workflowBranch && slug && commit.workflow && !commit.workflow.complete) {
        await this.finishWorkflowOverRest(workflowBranch, slug, options, commit.workflow.pull_request?.number ?? null);
      }
      return commit;
    } catch (error) {
      if (this.shouldFallBack(error)) {
        return super.persistFiles(dataFiles, mediaFiles, options);
      }
      throw error;
    }
  }
  async deleteFiles(paths, message) {
    if (!this.turboFetch) {
      return super.deleteFiles(paths, message);
    }
    try {
      await this.turboCommit({
        branch: this.branch,
        message,
        deletions: paths.map(path => path.replace(/^\/+/, ''))
      });
      return undefined;
    } catch (error) {
      if (this.shouldFallBack(error)) {
        return super.deleteFiles(paths, message);
      }
      throw error;
    }
  }
}